import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BaseDiv } from '../../common/BaseElements';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Z_INDICES } from '../../common/constants';

const MODAL_DEFAULT_Z_INDEX = Z_INDICES.TOP_OVERLAY;
const ESCAPE_KEY = `Escape`;
const KEYBOARD_EVENT_KEYDOWN = `keydown`;
export interface IModalState {
  dialog?: React.ReactNode;
  open: boolean;
  disableBackdropClick?: boolean;
  zIndex?: number;
  disableDismiss?: boolean;
  zoom?: number;
}

const ModalBackdrop = styled(BaseDiv)<IModalState>`
  position: fixed;
  width: ${({ zoom }) => 100 / (zoom ?? 1)}vw;
  height: ${({ zoom }) => 100 / (zoom ?? 1)}vh;
  background-color: ${({ theme }) => theme.overlayColours.background};
  z-index: ${({ zIndex }) => zIndex || MODAL_DEFAULT_Z_INDEX};
  top: 0;
  left: 0;
`;

const ModalDialogFullScreen = styled(BaseDiv)<IModalState>`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ zIndex = MODAL_DEFAULT_Z_INDEX }) => zIndex + 1};
`;

const ModalDialogCenter = styled(BaseDiv)`
  max-height: inherit;
  max-width: 100%;
`;

const ModalDialogPadding = styled(BaseDiv)`
  padding: 1rem;
  box-sizing: border-box;
`;

const ModalDialogClickExclude = styled(BaseDiv)``;

type ModalStateListener = () => void;
type CreateModalStateResult = () => [IModalState, (next: IModalState) => void];

const initialModalState: IModalState = {
  open: false,
  disableBackdropClick: false,
  disableDismiss: false
};

/* *
 * Initialize app modal state.
 * Store all the subscribers, and call them on state change.
 * Return useModalState as a global hook, for futher access within an app.
 * */
const createModalState = (initialState: IModalState): CreateModalStateResult => {
  let modalState = initialState;
  const stateListeners = new Set<ModalStateListener>();

  const setModalState = (next: IModalState): void => {
    modalState = next;
    stateListeners.forEach((listener: ModalStateListener) => listener());
  };

  return () => {
    const [state, setState] = useState(modalState);
    useEffect(() => {
      const stateListener: ModalStateListener = (): void => setState(modalState);
      stateListeners.add(stateListener);
      return () => {
        stateListeners.delete(stateListener);
      };
    }, []);
    return [state, setModalState] as [IModalState, (next: IModalState) => void];
  };
};

/* *
 * Initialize modal state once, make it available to a consumer through useModalState.
 * Usage:
 * 1. const [modalState, setModalState] = useModalState(); (full case).
 * 2. const [, setModalState] = useModalState(); (only set case).
 * */
export const useModalState = createModalState(initialModalState);

export interface IPhoenixModalProviderProps {
  zoom?: number;
}

/* *
 * PhoenixModalProvider.
 * Should be injected as high as possible in the component tree.
 * */
export const PhoenixModalProvider: React.FC<IPhoenixModalProviderProps> = (props): JSX.Element => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [modalState, setModalState] = useModalState();
  const { open, dialog, disableBackdropClick, disableDismiss } = modalState;
  const { generateTestId } = useTestId(props, 'ModalProvider');

  const keyDownHandler = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === ESCAPE_KEY) {
        if (disableDismiss) return;
        setModalState({ open: false });
      }
    },
    [disableDismiss, setModalState]
  );

  const backdropClickHandler = ({ target }: React.MouseEvent<HTMLDivElement>): void => {
    if (disableDismiss) return;
    if (disableBackdropClick) return;
    /* istanbul ignore next */
    if (!contentRef.current?.contains(target as HTMLElement)) {
      setModalState({ open: false });
    }
  };

  useEffect(() => {
    document.addEventListener(KEYBOARD_EVENT_KEYDOWN, keyDownHandler);
    return () => {
      document.removeEventListener(KEYBOARD_EVENT_KEYDOWN, keyDownHandler);
    };
  }, [keyDownHandler]);

  // When setting open state, specifying the dialog is mandatory.
  // Not specifying the dialog when opening a modal is considered to be
  // wrong, and will result in not showing the modal completely.
  // The only reason why the prop itself is not marked as mandatory, is setting the closing state.
  // There the prop can be ommited.
  if (!open || !dialog) {
    document.body.style.overflowY = `auto`;
    return <></>;
  }

  document.body.style.overflowY = `hidden`;

  // <ModalDialogFullScreen> Full-screen container, max-height is 100% to support scrollY for long contents.
  // <ModalDialogCenter> Centered container, max-height is 100% to support scrollY for long contents.
  // <ModalDialogPadding> Added to apply proper 2rem paddings. Didn't use parent, cause bottom-padding isn't useful on containers with max height.
  // <ModalDialogClickExclude> Added to capture click outside action. Didn't use parent since the click would capture padding area as well.
  return (
    <>
      <ModalBackdrop zoom={props.zoom} {...modalState} {...generateTestId(`ModalBackdrop`)} />
      <ModalDialogFullScreen {...modalState} {...generateTestId()} onClick={backdropClickHandler}>
        <ModalDialogCenter>
          <ModalDialogPadding>
            <ModalDialogClickExclude {...generateTestId(`Content`)} ref={contentRef}>
              {dialog}
            </ModalDialogClickExclude>
          </ModalDialogPadding>
        </ModalDialogCenter>
      </ModalDialogFullScreen>
    </>
  );
};
